import { RefObject, useEffect } from 'react';
import { useLocation } from 'react-router';
import {
  CreatePermanentJobPostingFormSection,
  TCreatePermanentJobPostingPageLocationState,
} from '../Types';

type TSectionRefs = Partial<
  Record<CreatePermanentJobPostingFormSection, RefObject<HTMLDivElement | null>>
>;

interface IUseAutoScrollToSectionProps {
  /** Whether the page content is ready (data loaded) */
  isReady: boolean;
  /** Map of section enum values to their corresponding refs */
  sectionRefs: TSectionRefs;
  /** Offset from top of viewport in pixels (default: 100) */
  scrollOffset?: number;
}

/**
 * Hook to automatically scroll to a form section when navigating in edit mode
 * with a scrollTo target specified in the location state.
 *
 * @example
 * ```tsx
 * const scheduleRef = useRef<HTMLDivElement>(null);
 * const earningsRef = useRef<HTMLDivElement>(null);
 *
 * useAutoScrollToSection({
 *   sectionRefs: {
 *     [CreatePermanentJobPostingFormSection.Schedule]: scheduleRef,
 *     [CreatePermanentJobPostingFormSection.EarningsAndBenefits]: earningsRef,
 *   },
 *   isReady: !isLoading,
 * });
 * ```
 */
export const useAutoScrollToSection = ({
  sectionRefs,
  isReady,
  scrollOffset = 100,
}: IUseAutoScrollToSectionProps) => {
  const { state } = useLocation();
  const locationState = state as TCreatePermanentJobPostingPageLocationState | null;

  useEffect(() => {
    if (locationState?.editMode && locationState?.scrollTo && isReady) {
      const scrollToSection = () => {
        const targetRef = locationState.scrollTo ? sectionRefs[locationState.scrollTo] : undefined;
        if (targetRef?.current) {
          // Get the element's position
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - scrollOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      };

      // Small delay to ensure the page is fully rendered
      const timeoutId = setTimeout(scrollToSection, 100);
      return () => clearTimeout(timeoutId);
    }
    // Intentionally omitted from dependencies:
    // - sectionRefs: refs are stable and don't change between renders
    // - scrollOffset: stable configuration value, unlikely to change at runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.editMode, locationState?.scrollTo, isReady]);
};
